import * as adapter from '@/lib/supabaseAdapter';

export type AppRole = 'read_only' | 'guestlist' | 'marketing' | 'gm' | 'vip' | 'admin';

export interface UserRole {
  id: string;
  user_id: string;
  venue_id: string;
  role: AppRole;
  created_at: string;
  updated_at: string;
}

// Role hierarchy and permissions
export const ROLE_CONFIG: Record<
  AppRole,
  {
    label: string;
    description: string;
    canAccessGMReports: boolean;
    canAccessBilling: boolean;
    canManageTeam: boolean;
    canAccessVIP: boolean;
  }
> = {
  read_only: {
    label: 'Read Only',
    description: 'View-only access to Dashboard and Events',
    canAccessGMReports: false,
    canAccessBilling: false,
    canManageTeam: false,
    canAccessVIP: false,
  },
  guestlist: {
    label: 'Guestlist',
    description: 'Can access Calendar, Events, Marketing, and Guestlist features',
    canAccessGMReports: false,
    canAccessBilling: false,
    canManageTeam: false,
    canAccessVIP: false,
  },
  marketing: {
    label: 'Marketing',
    description: 'Can access all product features except Billing and VIP',
    canAccessGMReports: false,
    canAccessBilling: false,
    canManageTeam: false,
    canAccessVIP: false,
  },
  gm: {
    label: 'GM',
    description: 'Can access all features including GM Reports and VIP, except Billing',
    canAccessGMReports: true,
    canAccessBilling: false,
    canManageTeam: false,
    canAccessVIP: true,
  },
  vip: {
    label: 'VIP Team',
    description: 'Can access VIP Intelligence, customer profiles, and outreach tools',
    canAccessGMReports: false,
    canAccessBilling: false,
    canManageTeam: false,
    canAccessVIP: true,
  },
  admin: {
    label: 'Admin',
    description: 'Full access including Billing, VIP, and team management',
    canAccessGMReports: true,
    canAccessBilling: true,
    canManageTeam: true,
    canAccessVIP: true,
  },
};

// Routes that each role can access
export const ROLE_ROUTES: Record<AppRole, string[]> = {
  read_only: ['/dashboard', '/events', '/settings', '/account', '/account/*'],
  guestlist: [
    '/dashboard',
    '/calendar',
    '/events',
    '/marketing',
    '/marketing/*',
    '/hyped',
    '/hyped/*',
    '/insights',
    '/insights/*',
    '/analytics',
    '/analytics/*',
    '/settings',
    '/team',
    '/account',
    '/account/*',
  ],
  marketing: [
    '/dashboard',
    '/calendar',
    '/events',
    '/marketing',
    '/marketing/*',
    '/hyped',
    '/hyped/*',
    '/insights',
    '/insights/*',
    '/analytics',
    '/analytics/*',
    '/settings',
    '/team',
    '/account',
    '/account/*',
  ],
  gm: [
    '/dashboard',
    '/calendar',
    '/events',
    '/marketing',
    '/marketing/*',
    '/hyped',
    '/hyped/*',
    '/insights',
    '/insights/*',
    '/analytics',
    '/analytics/*',
    '/settings',
    '/team',
    '/account',
    '/account/*',
    '/admin/demo-data',
    '/crm',
    '/crm/*',
  ],
  vip: [
    '/dashboard',
    '/calendar',
    '/events',
    '/marketing',
    '/marketing/*',
    '/hyped',
    '/hyped/*',
    '/insights',
    '/insights/*',
    '/analytics',
    '/analytics/*',
    '/settings',
    '/account',
    '/account/*',
    '/crm',
    '/crm/*',
  ],
  admin: [
    '/dashboard',
    '/calendar',
    '/events',
    '/marketing',
    '/marketing/*',
    '/hyped',
    '/hyped/*',
    '/insights',
    '/insights/*',
    '/analytics',
    '/analytics/*',
    '/settings',
    '/team',
    '/account',
    '/account/*',
    '/admin/demo-data',
    '/pricing',
    '/crm',
    '/crm/*',
  ],
};

export async function getUserRole(
  venueId: string,
): Promise<{ role: AppRole | null; isOwner: boolean; error: Error | null }> {
  const user = await adapter.getCurrentUser();
  if (!user) {
    return {
      role: null,
      isOwner: false,
      error: new Error('Not authenticated'),
    };
  }

  const ownerUserId = await adapter.getVenueOwner(venueId);
  if (ownerUserId === user.id) {
    return { role: 'admin', isOwner: true, error: null };
  }

  try {
    const roleRow = await adapter.getUserRoleRow(user.id, venueId);
    return { role: roleRow?.role ?? null, isOwner: false, error: null };
  } catch (err) {
    return {
      role: null,
      isOwner: false,
      error: new Error(err instanceof Error ? err.message : 'Unknown error'),
    };
  }
}

export async function getVenueUserRoles(
  venueId: string,
): Promise<{ data: UserRole[]; error: Error | null }> {
  try {
    const data = await adapter.getVenueUserRolesRows(venueId);
    return { data: (data as unknown as UserRole[]) || [], error: null };
  } catch (err) {
    return {
      data: [],
      error: new Error(err instanceof Error ? err.message : 'Unknown error'),
    };
  }
}

export async function updateUserRole(
  userId: string,
  venueId: string,
  role: AppRole,
): Promise<{ success: boolean; error: Error | null }> {
  try {
    await adapter.upsertUserRole(userId, venueId, role);
    return { success: true, error: null };
  } catch (err) {
    return {
      success: false,
      error: new Error(err instanceof Error ? err.message : 'Unknown error'),
    };
  }
}

export async function removeUserRole(
  userId: string,
  venueId: string,
): Promise<{ success: boolean; error: Error | null }> {
  try {
    await adapter.deleteUserRole(userId, venueId);
    return { success: true, error: null };
  } catch (err) {
    return {
      success: false,
      error: new Error(err instanceof Error ? err.message : 'Unknown error'),
    };
  }
}

export function canAccessRoute(role: AppRole | null, path: string): boolean {
  if (!role) return false;

  const allowedRoutes = ROLE_ROUTES[role];
  return allowedRoutes.some((route) => {
    if (route.endsWith('/*')) {
      const basePath = route.slice(0, -2);
      return path === basePath || path.startsWith(`${basePath}/`);
    }
    return path === route;
  });
}
